/**
 * MetadataCard Component
 *
 * Card with an icon header used to group label/value rows on detail pages.
 * Pair with `InfoRow` for the rows themselves.
 */

import React from 'react';
import { motion } from 'framer-motion';

export interface MetadataCardProps {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  /** Stagger delay for the entrance animation, in seconds. */
  delay?: number;
  statusBadge?: React.ReactNode;
  highlight?: boolean;
  /** Solid background for the icon tile. Defaults to a red gradient. */
  iconColor?: string;
  className?: string;
}

export const MetadataCard: React.FC<MetadataCardProps> = ({
  title,
  icon: Icon,
  children,
  delay = 0,
  statusBadge,
  highlight = false,
  iconColor,
  className = '',
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`
        relative w-full rounded-2xl p-5 shadow-lg transition-all duration-300 backdrop-blur-md
        ${
          highlight
            ? 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
            : 'bg-white/90 hover:bg-white border border-gray-200/50'
        }
        ${className}
      `}
    >
      {statusBadge && (
        <div className="absolute top-4 right-4 z-10">{statusBadge}</div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            iconColor ? '' : 'bg-gradient-to-r from-red-500 to-red-600'
          }`}
          style={iconColor ? { backgroundColor: iconColor } : undefined}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
      </div>

      <div className="space-y-3">{children}</div>
    </motion.div>
  );
};

export default MetadataCard;
